import React, { useEffect, useState } from 'react';
import Toastify, { notifyError, notifySuccess } from "../../utils/tostfy";
import { Link } from 'react-router-dom';
import { getApiErrorMessage, handleApiCall } from '../../apis';
import { endpoint } from '../../utils/endpoints';
import PermissionGate from '../../Components/PermissionGate/PermissionGate';
import { MODULES } from '../../utils/permissions';
import EditIcon from '../../assets/image/edit-icon.svg'
import DeleteIcon from '../../assets/image/delete-icon.svg'
import EyeIcon from '../../assets/image/eye-icon.svg'
import LeftAngle from '../../assets/image/left-angle.svg'
import RightAngle from '../../assets/image/right-angle.svg'
import { SEARCH_MAX_LENGTH, clampSearchInput } from "../../utils/search";
import PageNumberInput from "../../Components/Pagination/PageNumberInput";

const formatFuelPumpNames = (ref) => {
    if (!ref) return '-';
    if (Array.isArray(ref)) {
        const parts = ref.map((x) => {
            if (typeof x === 'object' && x !== null) {
                return (
                    x.fuel_pump_name ||
                    x.name ||
                    (x.fuel_pump_id && typeof x.fuel_pump_id === 'object'
                        ? x.fuel_pump_id.fuel_pump_name || x.fuel_pump_id.name
                        : '') ||
                    ''
                );
            }
            return String(x);
        }).filter(Boolean);
        return parts.length ? parts.join(', ') : '-';
    }
    if (typeof ref === 'object' && ref !== null) {
        return ref.fuel_pump_name || ref.name || '-';
    }
    return '-';
};

const mapFuelOperatorRow = (r) => ({
    id: r?._id || r?.id || '',
    employeeId: r?.employee_id ?? r?.employeeId ?? '-',
    name: r?.full_name ?? r?.name ?? '-',
    email: r?.email ?? '-',
    mobile: (() => {
        const code = (r?.country_code || '').trim();
        const num = (r?.mobile_number ?? r?.mobile ?? '').toString().trim();
        if (code && num) return `${code} ${num}`;
        return num || code || '-';
    })(),
    mobileDigits: (r?.mobile_number ?? r?.mobile ?? '')
        .toString()
        .replace(/\D/g, '')
        .slice(0, 10),
    countryCode: (r?.country_code ?? '+91').trim() || '+91',
    miplLocationId: (() => {
        const loc = r?.mipl_location_id;
        if (loc && typeof loc === 'object') return String(loc._id || loc.id || '');
        if (loc != null && loc !== '') return String(loc);
        return '';
    })(),
    fuelPumpIds: Array.isArray(r?.fuel_pump_data)
        ? r.fuel_pump_data.map((fp) => fp?._id || fp?.id).filter(Boolean)
        : [],
    location:
        r?.mipl_location_id?.name ||
        r?.mipl_location?.name ||
        r?.location_name ||
        r?.location ||
        '-',
    fuelPumpName: formatFuelPumpNames(
        r?.fuel_pump_data ?? r?.fuel_pump_id ?? r?.fuel_pumps ?? r?.fuelPump
    ),
    statusCode: r?.status === 1 ? 1 : 0,
    status:
        r?.status === 1 || r?.status === 'Active'
            ? 'Active'
            : r?.status === 0 || r?.status === 'Inactive'
              ? 'Inactive'
              : typeof r?.status === 'string'
                ? r.status
                : 'Inactive',
});

const FuelOperatorManagement = () => {
    const [rows, setRows] = useState([]);
    const [listLoading, setListLoading] = useState(true);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [deleteId, setDeleteId] = useState(null);
    const [statusUpdatingId, setStatusUpdatingId] = useState('');

    const [itemsPerPage, setItemsPerPage] = useState(10);

    useEffect(() => {
        setCurrentPage(1);
    }, [search]);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search), 400);
        return () => clearTimeout(t);
    }, [search]);
    const searchForApi = (() => {
        const s = clampSearchInput(debouncedSearch).trim();
        if (!s) return '';
        if (/^\+?[\d\s-]+$/.test(s)) return s.replace(/\D/g, '');
        return s;
    })();
    const fetchFuelOperatorList = async () => {
        setListLoading(true);
        try {
            const response = await handleApiCall(
                'get',
                `${endpoint.listOfFuelOperator}?search=${encodeURIComponent(searchForApi)}&page=${currentPage}&limit=${itemsPerPage}`,
                {}
            );
            if (response?.data?.code === 200) {
                const d = response?.data?.data || {};
                const raw =
                    d?.fuel_operator_data ||
                    d?.fuelOperator_data ||
                    d?.fuel_operators ||
                    (Array.isArray(d) ? d : null);
                const list = Array.isArray(raw) ? raw : [];
                const mapped = list.map(mapFuelOperatorRow);
                setRows(mapped);
                setTotalRecords(d?.total ?? mapped.length);
                setTotalPages(Math.max(1, d?.total_pages ?? 1));
            } else {
                // const errMsg =
                //     response?.response?.data?.errorMessage ||
                //     response?.response?.data?.message ||
                //     response?.data?.message ||
                //     'Failed to load fuel operators';
                // notifyError(errMsg);
                notifyError(getApiErrorMessage(response, 'Failed to load fuel operators'));
                setRows([]);
                setTotalRecords(0);
                setTotalPages(1);
            }
        } catch {
            notifyError('Failed to load fuel operators');
            setRows([]);
            setTotalRecords(0);
            setTotalPages(1);
        } finally {
            setListLoading(false);
        }
    };

    useEffect(() => {
        fetchFuelOperatorList();
    }, [debouncedSearch, currentPage, itemsPerPage]);

    const handleFuelOperatorStatusToggle = async (fuelOperatorId, currentStatus) => {
        const nextStatus = currentStatus === 1 ? 0 : 1;
        if (!fuelOperatorId) return;
        setStatusUpdatingId(fuelOperatorId);
        try {
            const response = await handleApiCall('put', endpoint.updateStatusFuelOperator, {
                fuel_operator_id: fuelOperatorId,
                status: nextStatus,
            });
            if (response?.data?.code === 200) {
                setRows((prev) =>
                    prev.map((r) =>
                        r.id === fuelOperatorId
                            ? {
                                  ...r,
                                  statusCode: nextStatus,
                                  status: nextStatus === 1 ? 'Active' : 'Inactive',
                              }
                            : r
                    )
                );
                notifySuccess(response?.data?.message || 'Fuel operator status updated');
                return;
            }
            const errMsg =
                response?.response?.data?.errorMessage ||
                response?.response?.data?.message ||
                response?.data?.message ||
                'Could not update fuel operator status';
            notifyError(errMsg);
        } catch {
            notifyError('Something went wrong. Please try again.');
        } finally {
            setStatusUpdatingId('');
        }
    };

    const displayFrom = totalRecords === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
    const displayTo = Math.min(currentPage * itemsPerPage, totalRecords);

    const handleDelete = () => {
        setRows((prev) => prev.filter((r) => r.id !== deleteId));
        setTotalRecords((prev) => Math.max(0, prev - 1));
        setDeleteId(null);
    };

    return (
        <>
        <Toastify/>
            <div className="tab-pane-content !mt-4 md:mt-7">

                <div className='flex gap-x-2.5 mb-3 md:mb-4'>
                    
                    <input
                        type="text"
                        placeholder="Search..."
                        className="border px-3 py-2 rounded-lg outline-none text-sm searchIcon bg-white focus:!shadow-sm"
                        value={search}
                        maxLength={SEARCH_MAX_LENGTH}

                        onChange={(e) => setSearch(clampSearchInput(e.target.value))}
                    />

                    <PermissionGate module={MODULES.FUEL_OPERATOR} action="add">
                        <Link className="btn btn-danger ml-auto max-[767px]:px-4 max-[767px]:text-xs" to="/fuel-operator-management/add-new">
                            + Add New
                        </Link>
                    </PermissionGate>
                </div>

                {/* Table */}
                <div className="common-table-wrapper m-0">
                    <div className="table-responsive">
                        <table>
                            <thead>
                                <tr>
                                    <th>S.No.</th>
                                    <th>Employee ID</th>
                                    <th>Name</th>
                                    <th>Email</th>
                                    <th>Mobile Number</th>
                                    <th>MIPL Office Location</th>
                                    <th>Fuel Pump Name</th>
                                    <th>Status</th>
                                    <th>Action</th>
                                </tr>
                            </thead>

                            <tbody>
                                {listLoading && (
                                    <tr>
                                        <td colSpan={9} className="text-center py-4 text-gray-500">
                                            Loading...
                                        </td>
                                    </tr>
                                )}
                                {!listLoading && rows.map((row, index) => (
                                    <tr key={row.id || `row-${index}`}>
                                        <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                                        <td>{row.employeeId}</td>
                                        <td>{row.name}</td>
                                        <td>{row.email}</td>
                                        <td>{row.mobile}</td>
                                        <td>{row.location}</td>
                                        <td><div className='max-w-32 text-wrap leading-tight'>{row.fuelPumpName}</div></td>
                                        <td>{row.status}</td>

                                        {/* Actions */}
                                        <td>
                                            <div className="flex items-center justify-center gap-2">
                                                <PermissionGate module={MODULES.FUEL_OPERATOR} action="edit">
                                                    <label className="relative inline-flex items-center cursor-pointer w-9 min-w-9 h-5 bg-[#E11818] rounded-full my-0 [&:has(input:checked)]:bg-[#17B517] [&:has(input:checked)_.absolute]:!left-[17px] transition-all">
                                                        <input
                                                            type="checkbox"
                                                            className="hidden"
                                                            checked={row.statusCode === 1}
                                                            onChange={() => handleFuelOperatorStatusToggle(row.id, row.statusCode)}
                                                            disabled={statusUpdatingId === row.id || listLoading}
                                                        />
                                                        <div className="absolute left-[3px] top-0.5 w-4 h-4 bg-white rounded-full transition-all" />
                                                    </label>
                                                </PermissionGate>
                                                <PermissionGate module={MODULES.FUEL_OPERATOR} action="edit">
                                                    <Link to="/fuel-operator-management/edit" state={{ operator: row }}><img src={EditIcon} className='h-7 min-w-[22px]' alt="Edit"/></Link>
                                                </PermissionGate>
                                                <PermissionGate module={MODULES.FUEL_OPERATOR} action="view">
                                                    <Link
                                                        to={`/fuel-operator-management/view?fuel_operator_id=${encodeURIComponent(row.id)}`}
                                                    >
                                                        <img src={EyeIcon} className='h-7 min-w-[22px]' alt="View"/>
                                                    </Link>
                                                </PermissionGate>
                                                {/* <span className="cursor-pointer" onClick={() => setDeleteId(row.id)} role="presentation">
                                                    <img src={DeleteIcon} className='h-7 min-w-[22px]' alt="Delete"/>
                                                </span> */}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {!listLoading && rows.length === 0 && (
                                    <tr>
                                        <td colSpan={9} className="text-center py-4 text-gray-500">
                                            No fuel operators found
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Pagination */}
                <div className="flex gap-x-4 items-center mt-4">

                    <select
                        className='p-2 shadow-sm outline-none max-[767px]:text-xs'
                        value={itemsPerPage}
                        onChange={(e) => {
                            setItemsPerPage(Number(e.target.value));
                            setCurrentPage(1);
                        }}
                    >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                    </select>
                    <div className="text-xs md:text-sm text-gray-500 ml-auto">
                        {listLoading
                            ? '—'
                            : totalRecords === 0
                              ? '0 records found'
                              : `${displayFrom}-${displayTo} of ${totalRecords} records found`}
                    </div>
                    <div className="flex items-center gap-2 md:pr-8">
                        <button
                            type="button"
                            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                            disabled={listLoading || currentPage === 1}
                            className={`!bg-primary text-white md:w-[35px] md:h-[35px] w-[25px] h-[25px] rounded-full disabled:opacity-50 inline-flex justify-center items-center ${currentPage === 1 ? '' : 'cursor-pointer'}`}
                        >
                            <img src={LeftAngle} className='invert grayscale brightness-100' alt=""/>
                        </button>
                        <PageNumberInput
              page={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              className="h-[35px] w-10 md:w-12 rounded border border-gray-300 text-center text-sm font-UbuntuMedium shadow-sm outline-none disabled:cursor-not-allowed disabled:bg-gray-100 disabled:opacity-70"
            />
                        <span className="text-sm text-gray-600 text-nowrap">
                            of {totalPages}
                        </span>
                        <button
                            type="button"
                            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                            disabled={listLoading || currentPage === totalPages || totalRecords === 0}
                            className="!bg-primary text-white md:w-[35px] md:h-[35px] w-[25px] h-[25px] rounded-full disabled:opacity-50 inline-flex justify-center items-center cursor-pointer"
                        >
                            <img src={RightAngle} className='invert grayscale brightness-100' alt=""/>
                        </button>

                    </div>
                </div>

            </div>

            {/* Delete Modal */}
            {deleteId && (
                <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-[200]">
                    <div className="bg-white p-6 rounded-xl">
                        <p className="mb-4 text-center font-bold">
                            Are you sure you want to delete?
                        </p>

                        <div className="flex justify-center gap-4">
                            <button
                                type="button"
                                className="border px-4 py-2 rounded"
                                onClick={() => setDeleteId(null)}
                            >
                                No
                            </button>

                            <button
                                type="button"
                                className="bg-red-500 text-white px-4 py-2 rounded"
                                onClick={handleDelete}
                            >
                                Yes
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default FuelOperatorManagement;
